import { allCommunitySearchSettingsKeys, DefaultCommunitySearch } from "@sp-crm/core";
import { Feature } from "components/feature";
import { ContentFull, ContentHeader, ContentSubheader } from "components/layout";
import { BulkCommunityToggle } from "components/shared/bulk-community-toggle";
import { CommunityCardContactModeSelect } from "components/shared/community-card-contact-mode-select";
import { DefaultCommunitySearchEditor } from "components/shared/default-community-search-editor";
import { Checkbox } from "components/ui/checkbox";
import { fancyConfirm } from "components/ui/fancy-confirm";
import React, { ChangeEvent, useCallback, useMemo } from "react";
import { useCurrentRegion } from "store/selectors/hooks";
import { useRegionSettings } from "store/selectors/settings";

export const RegionSettingsCommunities: React.FC<unknown> = () => {
    const {
        settings,
        putSetting,
        putSettings,
        atLeastOneOverridden,
        deleteSettings,
        status,
    } = useRegionSettings();
    const region = useCurrentRegion();

    const overridden = useMemo(
        () => atLeastOneOverridden(allCommunitySearchSettingsKeys),
        [atLeastOneOverridden],
    );

    const handleDefaultCommunitySearchChange = useCallback(
        (value: DefaultCommunitySearch) => {
            putSetting("defaultCommunitySearch", value);
        },
        [putSetting],
    );

    const handleOverrideChange = useCallback(
        async (e: ChangeEvent<HTMLInputElement>) => {
            if (e.target.checked) {
                await putSettings(
                    allCommunitySearchSettingsKeys.map(key => ({
                        key,
                        value: settings[key],
                    })),
                );
            } else {
                const confirmed = await fancyConfirm(
                    "Are you sure?",
                    `This will delete all specific community search settings for ${region?.name}.`,
                    "Yes, delete",
                    "No, cancel",
                );
                if (confirmed) {
                    await deleteSettings(allCommunitySearchSettingsKeys);
                }
            }
        },
        [putSettings, deleteSettings, settings, region],
    );

    if (status !== "success") {
        return null;
    }

    return (
        <Feature name="placement">
            <div className="space-y-4 lg:space-y-8">
                <ContentFull>
                    <ContentHeader>{`Community search - ${region?.name}`}</ContentHeader>
                    <ContentSubheader>
                        Configure default search settings for this region
                    </ContentSubheader>
                    <Checkbox
                        checked={overridden}
                        onChange={handleOverrideChange}
                        label={
                            <p>
                                Use specific community search settings for{" "}
                                <strong>{region?.name}</strong>
                            </p>
                        }
                    />
                    <div className="space-y-4 mt-4">
                        <BulkCommunityToggle
                            value={settings.enableBulkCommunityOptions}
                            onChange={value =>
                                putSetting("enableBulkCommunityOptions", value)
                            }
                            disabled={!overridden}
                        />
                        <DefaultCommunitySearchEditor
                            value={settings.defaultCommunitySearch}
                            onChange={handleDefaultCommunitySearchChange}
                            disabled={!overridden}
                        />
                        <CommunityCardContactModeSelect
                            value={settings.communityCardContactMode}
                            onChange={value =>
                                putSetting("communityCardContactMode", value)
                            }
                            disabled={!overridden}
                        />
                    </div>
                </ContentFull>
            </div>
        </Feature>
    );
};
